import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatNativeDateModule } from '@angular/material/core';
import { MatCalendar } from '@angular/material/datepicker';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { NavigationStart, Router } from '@angular/router';
import dayjs from 'dayjs';
import { Subscription } from 'rxjs';
import { AuthService } from '../../services/auth.service';
import { ClientCapabilityService, ClientCapabilities } from '../../services/client-capability.service';
import { ModalService } from '../../services/modal.service';
import { NotificationService } from '../../services/notification.service';
import { TruthManagement } from '../../services/truth-management';
import { SequencingRequestDetailsItem } from '../../models/truth-management.model';
import { PageHeaderComponent } from '../page-header/page-header.component';

type TimeFilter = 'today' | 'last7' | 'last30' | 'custom';
type LoanModalMode = 'add' | 'edit';
const SHARED_DATE_FILTER_KEY = 'docsila.sharedDateFilter.operationsScope';
const ACCEPTED_UPLOAD_MIME_TYPES = new Set([
  'application/zip',
  'application/x-zip-compressed',
  'multipart/x-zip',
  'application/pdf',
]);
const ACCEPTED_UPLOAD_EXTENSIONS = ['.zip', '.pdf'] as const;

type PackageFieldType =
  | 'text'
  | 'textarea'
  | 'number'
  | 'checkbox'
  | 'radio'
  | 'select'
  | 'multiselect'
  | 'date'
  | 'datetime'
  | 'json';

interface PackageFieldOption {
  value: string;
  label: string;
}

interface PackageField {
  key: string;
  label: string;
  field_type: PackageFieldType;
  description?: string;
  placeholder?: string;
  required?: boolean;
  default?: unknown;
  rows?: number;
  is_list?: boolean;
  options?: PackageFieldOption[];
  conditional_on?: Record<string, unknown>;
}

type SortDirection = 'asc' | 'desc';
type SortColumn =
  | 'fileName'
  | 'pageCount'
  | 'miscPages'
  | 'docs'
  | 'durationSeconds'
  | 'lastModifiedOn'
  | 'status';

interface IndexingRow {
  loanNumber: string;
  fileName: string;
  clientId: string;
  requestId: string;
  lastModifiedOn: string;
  lastModifiedBy: string;
  pageCount: number | null;
  miscPages: string;
  docs: string;
  durationSeconds: number | null;
  status: 'Active' | 'Pending' | 'On Hold' | 'Completed' | 'Failed';
  hilStatus: 'completed' | 'failed' | 'needed' | 'unknown';
  actions: string;
  statusDetails?: {
    clientRequestId: string;
    requestId: string;
    owner: string;
    ocrCost: number | null;
    llmCost: number | null;
    fileName: string;
    model: string;
    message: string;
    pageCount: number | null;
  };
}

interface SourceLoanContext {
  loanNumber: string;
  row: IndexingRow;
  requestId?: string;
}

interface SharedDateFilterState {
  selectedTimeFilter: TimeFilter;
  startDate: string;
  endDate: string;
  startDateDisplay: string;
  endDateDisplay: string;
  updatedAt: number;
}

const SOURCE_LOAN_CONTEXT_STORAGE_KEY = 'source:selected-loan-context';

@Component({
  selector: 'app-indexing',
  standalone: true,
  imports: [CommonModule, FormsModule, MatCalendar, MatNativeDateModule, PageHeaderComponent],
  templateUrl: './indexing.component.html',
  styleUrls: ['./indexing.component.scss'],
})
export class IndexingComponent implements OnInit, OnDestroy {
  private sequencingRequestSub?: Subscription;
  private routerEventSub?: Subscription;
  private currentClientSub?: Subscription;
  private capabilitiesSub?: Subscription;
  private hasEmittedCapabilities = false;
  showContactAdminEmpty = false;
  capabilities: ClientCapabilities | null = null;
  private lastKnownClientId: string | null = null;
  private sequencingRequestEpoch = 0;
  isAuthenticated = false;
  isRoleResolved = false;
  isLoading = false;

  searchTerm = '';
  sortColumn: SortColumn | null = null;
  sortDirection: SortDirection = 'asc';

  allRows: IndexingRow[] = [];
  displayedRows: IndexingRow[] = [];

  isAddLoanModalOpen = false;
  loanModalMode: LoanModalMode = 'add';
  addLoanLoanNumber = '';
  addLoanBorrowerNames = '';
  addLoanPriorityOptions = ['High', 'Medium', 'Low'];
  addLoanChecklistTypeOptions = ['Standard', 'Enhanced'];
  addLoanSelectedPriority = 'High';
  addLoanSelectedChecklistType = 'Standard';
  addLoanSelectedFileName = '';
  addLoanFileError = '';
  addLoanFormError = '';
  isAddLoanDropActive = false;

  addPackageSchema: PackageField[] = [];
  addPackageValues: Record<string, unknown> = {};
  addPackageChipDrafts: Record<string, string | undefined> = {};
  addPackageKvDrafts: Record<string, { key: string; value: string } | undefined> = {};
  addPackageJsonErrors: Record<string, string> = {};
  addPackageFieldErrors: Record<string, string> = {};
  isPackageSchemaLoading = false;
  isSubmittingAddPackage = false;
  private addPackageJsonDrafts: Record<string, string> = {};
  private addPackageSchemaSub?: Subscription;
  private addPackageSubmitSub?: Subscription;

  @ViewChild('addLoanZipInput') addLoanZipInput?: ElementRef<HTMLInputElement>;
  private addLoanSelectedFile: File | null = null;

  selectedTimeFilter: TimeFilter = 'today';
  startDate = '';
  endDate = '';
  startDateDisplay = '';
  endDateDisplay = '';
  endDateInvalid = false;
  dateRangeValidationMessage = '';
  private hasShownInvalidDateRangeToast = false;
  startHour12 = '12';
  startMinute = '00';
  startPeriod: 'AM' | 'PM' = 'AM';
  endHour12 = '12';
  endMinute = '00';
  endPeriod: 'AM' | 'PM' = 'AM';
  readonly hourOptions = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'));
  readonly minuteOptions = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));
  activeDateTimePopup: 'start' | 'end' | null = null;
  popupDate: Date | null = null;
  popupHour12 = '12';
  popupMinute = '00';
  popupPeriod: 'AM' | 'PM' = 'AM';
  private popupDirty = false;
  activePackageDatePopupKey: string | null = null;
  packageDateDisplayByKey: Record<string, string> = {};
  packageDateSelectedByKey: Record<string, Date | null> = {};
  packageDateInvalidByKey: Record<string, boolean> = {};
  isStatusDetailsPopupOpen = false;
  activeStatusDetailsRow: IndexingRow | null = null;
  statusDetailsPopupMaxHeight = 'none';
  statusDetailsPopupPosition = { left: 0, top: 0 };
  @ViewChild('statusDetailsPopup') statusDetailsPopup?: ElementRef<HTMLElement>;

  constructor(
    private sanitizer: DomSanitizer,
    public router: Router,
    public authService: AuthService,
    private modalService: ModalService,
    private truthManagement: TruthManagement,
    private notificationService: NotificationService,
    private cdr: ChangeDetectorRef,
    private clientCapability: ClientCapabilityService,
  ) {}

  ngOnInit(): void {
    this.isAuthenticated = this.authService.hasValidToken();

    this.authService.userRole$.subscribe((role) => {
      if (role !== null) {
        this.isRoleResolved = true;
      }
    });

    this.allRows = [];
    this.displayedRows = [];

    this.routerEventSub = this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart && !this.isSharedDateFilterRoute(event.url)) {
        sessionStorage.removeItem(SHARED_DATE_FILTER_KEY);
      }
    });

    const sharedState = this.loadSharedDateFilterState();
    if (sharedState) {
      this.applySharedDateFilterState(sharedState);
    } else {
      this.applyPresetDateWindow('today');
      this.saveSharedDateFilterState();
    }

    this.lastKnownClientId = this.authService.getCurrentClient()?.client_id ?? null;
    this.fetchSequencingRequests();

    this.currentClientSub = this.authService.currentClient$.subscribe((client) => {
      const nextClientId = client?.client_id ?? null;
      if (nextClientId === this.lastKnownClientId) {
        return;
      }
      this.lastKnownClientId = nextClientId;
      this.fetchSequencingRequests();
      if (this.isAddLoanModalOpen) {
        this.fetchAddPackageSchema();
      }
    });

    this.capabilitiesSub = this.clientCapability.capabilities$.subscribe((caps) => {
      this.capabilities = caps;
      if (!caps) {
        this.showContactAdminEmpty = false;
        return;
      }
      this.showContactAdminEmpty = !caps.sequencingEnabled;
      this.hasEmittedCapabilities = true;
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.sequencingRequestSub?.unsubscribe();
    this.routerEventSub?.unsubscribe();
    this.addPackageSchemaSub?.unsubscribe();
    this.addPackageSubmitSub?.unsubscribe();
    this.currentClientSub?.unsubscribe();
    this.capabilitiesSub?.unsubscribe();
  }

  private getCurrentClientId(): string {
    return this.authService.getCurrentClient()?.client_id ?? '';
  }

  logout(): void {
    this.modalService.showLogoutConfirmation(() => {
      this.authService.logout();
      this.isAuthenticated = false;
    });
  }

  navigateToAdminSettings(): void {
    this.router.navigate(['/admin-settings']);
  }

  navigateToStatsDashboard(): void {
    this.router.navigate(['/stats-dashboard']);
  }

  navigateToEvaluationDashboard(): void {
    this.router.navigate(['/dashboard']);
  }

  navigateToOperationsRequestAnalytics(): void {
    this.router.navigate(['/operations-request-analytics']);
  }

  navigateToSource(row: IndexingRow): void {
    if (row.status !== 'Completed') { return; }
    const sourceLoanKey = (
      row.loanNumber ||
      row.requestId ||
      row.clientId ||
      row.fileName ||
      'source'
    ).trim();

    const selectedLoanContext: SourceLoanContext = {
      loanNumber: sourceLoanKey,
      row,
      requestId: row.requestId || undefined,
    };

    try {
      sessionStorage.setItem(SOURCE_LOAN_CONTEXT_STORAGE_KEY, JSON.stringify(selectedLoanContext));
    } catch {
      // If storage is unavailable, route state still carries the clicked context.
    }

    this.router.navigate(['/source'], {
      queryParams: {
        loanNumber: sourceLoanKey,
        requestId: row.requestId || undefined,
      },
      state: { selectedLoanContext },
    });
  }

  onOperationalHealthToggleChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    if (!target.checked) {
      this.navigateToOperationsRequestAnalytics();
    }
  }

  onAddLoansClick(): void {
    this.openAddLoanModal();
  }

  openAddLoanModal(): void {
    this.loanModalMode = 'add';
    this.resetAddLoanModalForm();
    this.addPackageSchema = [];
    this.addPackageValues = {};
    this.addPackageChipDrafts = {};
    this.addPackageKvDrafts = {};
    this.addPackageFieldErrors = {};
    this.addPackageJsonErrors = {};
    this.isAddLoanModalOpen = true;
    this.fetchAddPackageSchema();
  }

  onEditLoanClick(row: IndexingRow): void {
    this.loanModalMode = 'edit';
    this.resetAddLoanModalForm();
    this.prefillLoanModalForEdit(row);
    this.isAddLoanModalOpen = true;
  }

  get loanModalHeaderText(): string {
    return this.loanModalMode === 'edit' ? 'Edit Package' : 'Add Package';
  }

  get loanModalPrimaryActionText(): string {
    return this.loanModalMode === 'edit' ? 'Save' : 'Add Package';
  }

  requestCloseAddLoanModal(): void {
    this.closeAddLoanModal();
  }

  closeAddLoanModal(): void {
    this.isAddLoanModalOpen = false;
    this.resetAddLoanModalForm();
    this.addPackageSchemaSub?.unsubscribe();
    this.addPackageSubmitSub?.unsubscribe();
    this.isPackageSchemaLoading = false;
    this.isSubmittingAddPackage = false;
    this.addPackageSchema = [];
    this.addPackageValues = {};
    this.addPackageChipDrafts = {};
    this.addPackageKvDrafts = {};
    this.addPackageFieldErrors = {};
    this.addPackageJsonErrors = {};
    this.addPackageJsonDrafts = {};
  }

  onAddLoanModalBackdropClick(): void {
    return;
  }

  triggerAddLoanFilePicker(): void {
    this.addLoanZipInput?.nativeElement.click();
  }

  onAddLoanUploadZoneKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    this.triggerAddLoanFilePicker();
  }

  onAddLoanFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.assignAddLoanUploadFromFileList(input.files);
    input.value = '';
  }

  onAddLoanUploadDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isAddLoanDropActive = true;
  }

  onAddLoanUploadDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isAddLoanDropActive = false;
  }

  onAddLoanUploadDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isAddLoanDropActive = false;
    this.assignAddLoanUploadFromFileList(event.dataTransfer?.files ?? null);
  }

  onSubmitAddLoan(): void {
    if (this.loanModalMode === 'add') {
      if (!this.validateAddPackageForm()) {
        return;
      }
      this.submitAddPackage();
      return;
    }

    if (!this.validateAddLoanForm()) {
      return;
    }
    this.closeAddLoanModal();
  }

  private buildAddPackageMetadataPayload(): Record<string, unknown> {
    const payload: Record<string, unknown> = { ...this.addPackageValues };
    for (const field of this.addPackageSchema) {
      if (field.is_list) {
        payload[field.key] = this.asStringArray(payload[field.key])
          .map((v) => v.trim())
          .filter((v) => v.length > 0);
      }
    }
    return payload;
  }

  private submitAddPackage(): void {
    if (!this.addLoanSelectedFile) {
      this.addLoanFileError = 'Please upload one .zip or .pdf file.';
      return;
    }

    this.isSubmittingAddPackage = true;
    this.addLoanFormError = '';
    this.addPackageSubmitSub?.unsubscribe();
    this.addPackageSubmitSub = this.truthManagement
      .indexAndExtractFiles(this.addLoanSelectedFile, this.getCurrentClientId(), this.buildAddPackageMetadataPayload())
      .subscribe({
        next: () => {
          this.isSubmittingAddPackage = false;
          this.closeAddLoanModal();
          this.fetchSequencingRequests();
          this.notificationService.showSuccess('Package created successfully');
        },
        error: () => {
          this.isSubmittingAddPackage = false;
          this.addLoanFormError = 'Unable to add package. Please try again.';
          this.cdr.detectChanges();
        },
      });
  }

  onTimeFilterChange(): void {
    this.endDateInvalid = false;
    this.dateRangeValidationMessage = '';
    this.hasShownInvalidDateRangeToast = false;

    if (this.selectedTimeFilter !== 'custom') {
      this.popupDirty = false;
      this.activeDateTimePopup = null;
    }

    if (this.selectedTimeFilter !== 'custom') {
      this.applyPresetDateWindow(this.selectedTimeFilter);
    }

    this.saveSharedDateFilterState();

    if (this.selectedTimeFilter !== 'custom') {
      this.fetchSequencingRequests();
    }
  }

  onDateChange(): void {
    this.syncTimePartsFromDateTimes();
    this.syncDateDisplayValues();
    this.selectedTimeFilter = 'custom';

    if (this.validateDateRange()) {
      this.saveSharedDateFilterState();
      this.fetchSequencingRequests();
    }
  }

  onDateInputChange(field: 'start' | 'end', event: Event): void {
    const input = event.target as HTMLInputElement;
    const formatted = this.normalizeDisplayDateTimeInput(input.value);

    if (field === 'start') {
      this.startDateDisplay = formatted;
    } else {
      this.endDateDisplay = formatted;
    }

    if (input.value !== formatted) {
      input.value = formatted;
    }

    this.endDateInvalid = false;
    this.dateRangeValidationMessage = '';
    this.hasShownInvalidDateRangeToast = false;
  }

  onDateInputKeydown(event: KeyboardEvent): void {
    const key = event.key;
    const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];

    if (allowedKeys.includes(key) || event.ctrlKey || event.metaKey) {
      return;
    }

    if (!/^[\d/:\s]$/.test(key) && !/^[aApPmM]$/.test(key)) {
      event.preventDefault();
    }
  }

  onDateInputBlur(field: 'start' | 'end'): void {
    const value = (field === 'start' ? this.startDateDisplay : this.endDateDisplay).trim();
    if (!value) {
      this.notificationService.showError('Invalid date picked. Please use MM/DD/YYYY hh:mm AM/PM format.');
      this.endDateInvalid = field === 'end';
      this.dateRangeValidationMessage = '';
      this.hasShownInvalidDateRangeToast = false;
      return;
    }

    const localDateTime = this.convertDisplayToLocalDateTime(value);
    if (!localDateTime) {
      this.notificationService.showError('Invalid date picked. Please use a valid MM/DD/YYYY hh:mm AM/PM date and time.');
      this.endDateInvalid = field === 'end';
      this.dateRangeValidationMessage = '';
      this.hasShownInvalidDateRangeToast = false;
      return;
    }

    const currentValue = field === 'start' ? this.startDate : this.endDate;
    if (field === 'start') {
      this.startDateDisplay = this.formatDateForDisplay(localDateTime);
    } else {
      this.endDateDisplay = this.formatDateForDisplay(localDateTime);
    }

    if (localDateTime === currentValue) {
      return;
    }

    if (field === 'start') {
      this.startDate = localDateTime;
    } else {
      this.endDate = localDateTime;
    }

    this.onDateChange();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const path = (event.composedPath?.() || []) as EventTarget[];
    if (this.activeDateTimePopup) {
      const isInsideDatePopup = path.some((target) => {
        if (!(target instanceof HTMLElement)) {
          return false;
        }

        return (
          target.classList.contains('date-time-popup-anchor') ||
          target.classList.contains('date-time-popup-panel') ||
          target.classList.contains('mat-calendar')
        );
      });

      if (!isInsideDatePopup) {
        this.closeDateTimePopup();
      }
    }

    if (this.isStatusDetailsPopupOpen) {
      const isInsideStatusPopup = path.some((target) => {
        if (!(target instanceof HTMLElement)) {
          return false;
        }

        return (
          target.classList.contains('status-inline-info') ||
          target.classList.contains('status-details-popup')
        );
      });

      if (!isInsideStatusPopup) {
        this.closeStatusDetailsPopup();
      }
    }
  }

  @HostListener('document:keydown.escape')
  onDocumentEscape(): void {
    this.closeDateTimePopup();
    this.closePackageDatePopup();
    this.closeStatusDetailsPopup();
  }

  @HostListener('document:mousedown', ['$event'])
  onDocumentMousedown(event: MouseEvent): void {
    if (this.activePackageDatePopupKey === null) { return; }

    const target = event.target;
    if (!(target instanceof HTMLElement)) { return; }

    const isInsidePackageDatePopup =
      !!target.closest('.package-date-field') ||
      !!target.closest('.package-date-popup') ||
      !!target.closest('mat-calendar') ||
      target.classList.contains('mat-calendar-body-cell-content') ||
      target.classList.contains('mat-calendar-period-button') ||
      target.classList.contains('mat-calendar-arrow') ||
      target.classList.contains('mat-calendar-previous-button') ||
      target.classList.contains('mat-calendar-next-button');

    if (!isInsidePackageDatePopup) {
      this.closePackageDatePopup();
    }
  }

  openPackageDatePopup(key: string, event?: Event): void {
    event?.stopPropagation();

    if (this.activePackageDatePopupKey === key) {
      this.closePackageDatePopup();
      return;
    }

    if (this.activeDateTimePopup) {
      this.closeDateTimePopup();
    }
    if (this.activePackageDatePopupKey && this.activePackageDatePopupKey !== key) {
      this.closePackageDatePopup();
    }

    this.activePackageDatePopupKey = key;

    const stored = this.addPackageValues[key];
    let seed: Date | null = null;
    if (typeof stored === 'string' && stored.trim()) {
      const parsed = this.parsePackageDateValue(stored);
      if (parsed) { seed = parsed; }
    }
    this.packageDateSelectedByKey[key] = seed;
    if (seed && !this.packageDateDisplayByKey[key]) {
      this.packageDateDisplayByKey[key] = this.formatPackageDateDisplay(seed);
    }
  }

  closePackageDatePopup(): void {
    this.activePackageDatePopupKey = null;
  }

  onPackageDateInputChange(key: string, event: Event): void {
    const input = event.target as HTMLInputElement;
    const sanitized = this.sanitizePackageDateInput(input.value);
    this.packageDateDisplayByKey[key] = sanitized;
    if (input.value !== sanitized) {
      input.value = sanitized;
    }
    this.packageDateInvalidByKey[key] = false;
  }

  private sanitizePackageDateInput(raw: string): string {
    return (raw ?? '').replace(/[^\d/]/g, '').slice(0, 10);
  }

  onPackageDateInputKeydown(event: KeyboardEvent): void {
    const key = event.key;
    const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
    if (allowedKeys.includes(key) || event.ctrlKey || event.metaKey) {
      return;
    }
    if (!/^[\d/]$/.test(key)) {
      event.preventDefault();
    }
  }

  onPackageDateInputBlur(key: string): void {
    const raw = (this.packageDateDisplayByKey[key] ?? '').trim();
    if (!raw) {
      this.addPackageValues[key] = '';
      this.packageDateSelectedByKey[key] = null;
      this.packageDateInvalidByKey[key] = false;
      return;
    }

    const parsed = this.parsePackageDateDisplay(raw);
    if (!parsed) {
      this.packageDateInvalidByKey[key] = true;
      this.notificationService.showError('Invalid date picked. Please use MM/DD/YYYY format.');
      return;
    }

    this.packageDateInvalidByKey[key] = false;
    this.packageDateSelectedByKey[key] = parsed;
    this.packageDateDisplayByKey[key] = this.formatPackageDateDisplay(parsed);
    this.addPackageValues[key] = this.formatPackageDateIso(parsed);
  }

  onPackageDateSelectedChange(key: string, date: Date | null): void {
    if (!date) {
      this.packageDateSelectedByKey[key] = null;
      this.addPackageValues[key] = '';
      this.packageDateDisplayByKey[key] = '';
      this.closePackageDatePopup();
      return;
    }

    this.packageDateSelectedByKey[key] = date;
    this.packageDateDisplayByKey[key] = this.formatPackageDateDisplay(date);
    this.addPackageValues[key] = this.formatPackageDateIso(date);
    this.packageDateInvalidByKey[key] = false;
    this.closePackageDatePopup();
  }

  private parsePackageDateDisplay(value: string): Date | null {
    const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(value.trim());
    if (!match) { return null; }
    const month = Number(match[1]);
    const day = Number(match[2]);
    const year = Number(match[3]);
    if (month < 1 || month > 12 || day < 1 || day > 31) { return null; }
    const date = new Date(year, month - 1, day);
    if (Number.isNaN(date.getTime()) || date.getMonth() !== month - 1 || date.getDate() !== day) { return null; }
    return date;
  }

  private parsePackageDateValue(value: string): Date | null {
    const trimmed = value.trim();
    if (!trimmed) { return null; }
    const asDisplay = this.parsePackageDateDisplay(trimmed);
    if (asDisplay) { return asDisplay; }
    const isoMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(trimmed);
    if (isoMatch) {
      const date = new Date(Number(isoMatch[1]), Number(isoMatch[2]) - 1, Number(isoMatch[3]));
      if (!Number.isNaN(date.getTime())) { return date; }
    }
    const fallback = new Date(trimmed);
    return Number.isNaN(fallback.getTime()) ? null : fallback;
  }

  private formatPackageDateDisplay(date: Date): string {
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const yyyy = String(date.getFullYear());
    return `${mm}/${dd}/${yyyy}`;
  }

  private formatPackageDateIso(date: Date): string {
    const yyyy = String(date.getFullYear());
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    this.closeStatusDetailsPopup();
  }

  onIndexingTableScroll(): void {
    this.closeStatusDetailsPopup();
  }

  onStatusInfoClick(event: MouseEvent, row: IndexingRow): void {
    event.stopPropagation();

    const triggerElement = event.currentTarget as HTMLElement | null;
    if (!triggerElement) {
      return;
    }

    if (this.isStatusDetailsPopupOpen && this.activeStatusDetailsRow === row) {
      this.closeStatusDetailsPopup();
      return;
    }

    this.activeStatusDetailsRow = row;
    this.isStatusDetailsPopupOpen = true;

    const rect = triggerElement.getBoundingClientRect();
    this.positionStatusDetailsPopup(rect, 256);
    this.cdr.detectChanges();

    setTimeout(() => {
      if (!this.isStatusDetailsPopupOpen || this.activeStatusDetailsRow !== row) {
        return;
      }

      const measuredHeight = this.statusDetailsPopup?.nativeElement.getBoundingClientRect().height ?? 256;
      this.positionStatusDetailsPopup(rect, measuredHeight);
      this.cdr.detectChanges();
    });
  }

  closeStatusDetailsPopup(): void {
    this.isStatusDetailsPopupOpen = false;
    this.activeStatusDetailsRow = null;
    this.statusDetailsPopupMaxHeight = 'none';
  }

  private positionStatusDetailsPopup(triggerRect: DOMRect, popupHeight: number): void {
    const popupWidth = 288;
    const viewportPadding = 8;
    const popupGap = 8;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let left = triggerRect.left + (triggerRect.width / 2) - (popupWidth / 2);
    left = Math.max(viewportPadding, Math.min(left, viewportWidth - popupWidth - viewportPadding));

    const availableBelow = Math.max(0, viewportHeight - viewportPadding - (triggerRect.bottom + popupGap));
    const availableAbove = Math.max(0, triggerRect.top - popupGap - viewportPadding);
    const placeBelow = availableBelow >= availableAbove;
    const maxHeight = placeBelow ? availableBelow : availableAbove;
    this.statusDetailsPopupMaxHeight = `${Math.floor(maxHeight)}px`;

    let top = triggerRect.bottom + popupGap;
    if (!placeBelow) {
      const visibleHeight = Math.min(popupHeight, maxHeight);
      top = triggerRect.top - popupGap - visibleHeight;
    }

    this.statusDetailsPopupPosition = { left, top };
  }

  displayStatusDetailValue(value: unknown): string {
    if (value === null || value === undefined) {
      return '-';
    }

    const text = String(value).trim();
    return text ? text : '-';
  }

  displayStatusDetailCost(value: number | null): string {
    if (value === null || value === undefined) {
      return '-';
    }

    return String(value);
  }

  openDateTimePopup(field: 'start' | 'end', event?: Event): void {
    event?.stopPropagation();

    if (this.activeDateTimePopup && this.activeDateTimePopup !== field) {
      this.closeDateTimePopup();
    }

    this.activeDateTimePopup = field;
    this.popupDirty = false;

    const source = field === 'start' ? this.startDate : this.endDate;
    const parsed = this.parseLocalDateTime(source);
    if (parsed) {
      this.popupDate = new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
      const isPm = parsed.getHours() >= 12;
      this.popupPeriod = isPm ? 'PM' : 'AM';
      this.popupHour12 = String(parsed.getHours() % 12 || 12).padStart(2, '0');
      this.popupMinute = String(parsed.getMinutes()).padStart(2, '0');
      this.removeCalendarNavTitles();
      return;
    }

    const now = new Date();
    this.popupDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    this.popupHour12 = '12';
    this.popupMinute = '00';
    this.popupPeriod = 'AM';
    this.removeCalendarNavTitles();
  }

  onPopupPanelClick(event: MouseEvent): void {
    event.stopPropagation();
    this.removeCalendarNavTitles();
  }

  closeDateTimePopup(event?: Event): void {
    event?.stopPropagation();
    if (this.popupDirty) {
      this.onDateChange();
    }
    this.activeDateTimePopup = null;
    this.popupDirty = false;
  }

  onPopupDateSelected(date: Date | null): void {
    if (!date) {
      return;
    }

    this.popupDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    this.commitPopupSelection();
  }

  onPopupTimePartChange(): void {
    this.commitPopupSelection();
  }

  getPopupMinDate(field: 'start' | 'end'): Date | null {
    if (field === 'start') {
      return null;
    }

    const start = this.parseLocalDateTime(this.startDate);
    return start ? new Date(start.getFullYear(), start.getMonth(), start.getDate()) : null;
  }

  getPopupMaxDate(field: 'start' | 'end'): Date | null {
    if (field === 'end') {
      return null;
    }

    return this.getTodayDateForPopup();
  }

  getTodayDateForPopup(): Date {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }

  isPopupOpen(field: 'start' | 'end'): boolean {
    return this.activeDateTimePopup === field;
  }

  onSearchChange(): void {
    this.applyFiltersAndSort();
  }

  sortBy(column: SortColumn): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }

    this.applyFiltersAndSort();
  }

  getSortIcon(column: SortColumn): SafeHtml {
    if (this.sortColumn !== column) {
      return this.sanitizer.bypassSecurityTrustHtml('<div style="display:flex;flex-direction:column;align-items:center;line-height:0.8;font-family:Arial,sans-serif"><span>&#9650;</span><span>&#9660;</span></div>');
    }

    return this.sanitizer.bypassSecurityTrustHtml(this.sortDirection === 'asc' ? '&#9650;' : '&#9660;');
  }

  getStatusClass(status: IndexingRow['status']): string {
    if (status === 'Completed') return 'status-completed';
    if (status === 'Failed') return 'status-failed';
    return 'status-in-progress';
  }

  getStatusLabel(status: IndexingRow['status']): string {
    if (status === 'Completed') return 'Completed';
    if (status === 'Failed') return 'Failed';
    return 'In progress';
  }

  private normalizeHilStatus(raw: unknown): IndexingRow['hilStatus'] {
    if (typeof raw !== 'string') { return 'unknown'; }
    const upper = raw.trim().toUpperCase();
    if (upper === 'COMPLETED') { return 'completed'; }
    if (upper === 'FAILED')    { return 'failed'; }
    if (upper === 'NEEDED')    { return 'needed'; }
    return 'unknown';
  }

  getHilStatusClass(row: IndexingRow): string {
    switch (row.hilStatus) {
      case 'completed': return 'status-completed';
      case 'failed':    return 'status-failed';
      case 'needed':    return 'status-needed';
      case 'unknown':   return this.getStatusClass(row.status);
      default:          return this.getStatusClass(row.status);
    }
  }

  getHilStatusLabel(row: IndexingRow): string {
    switch (row.hilStatus) {
      case 'completed': return 'Completed';
      case 'failed':    return 'HIL Failed';
      case 'needed':    return 'Under Review';
      case 'unknown':   return this.getStatusLabel(row.status);
      default:          return this.getStatusLabel(row.status);
    }
  }

  formatDate(value: string): string {
    const normalizedValue = String(value || '').trim();
    if (!normalizedValue) {
      return '-';
    }

    const parsedDate = this.parseApiTimestamp(normalizedValue);
    if (!parsedDate) {
      return '-';
    }

    const timestamp = dayjs(parsedDate);
    if (!timestamp.isValid()) {
      return '-';
    }

    const now = dayjs();
    const diffMinutes = now.diff(timestamp, 'minute');

    if (diffMinutes >= 0 && diffMinutes < 60) {
      if (diffMinutes < 1) {
        return 'just now';
      }
      return `${diffMinutes} min${diffMinutes === 1 ? '' : 's'} ago`;
    }

    const diffHours = now.diff(timestamp, 'hour', true);
    if (diffHours >= 0 && diffHours < 24) {
      return timestamp.format('h:mm A');
    }

    const dayDiff = now.startOf('day').diff(timestamp.startOf('day'), 'day');
    if (dayDiff === 1) {
      return `Yesterday ${timestamp.format('h:mm A')}`;
    }

    return timestamp.format('MMM D, YYYY h:mm A');
  }

  private parseApiTimestamp(rawValue: string): Date | null {
    const value = String(rawValue || '').trim();
    if (!value) {
      return null;
    }

    const hasTimezone = /(?:[zZ]|[+-]\d{2}:?\d{2})$/.test(value);
    const normalized = hasTimezone ? value : `${value}Z`;
    const parsed = new Date(normalized);

    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  private applyFiltersAndSort(): void {
    const query = this.searchTerm.trim().toLowerCase();

    let filtered = this.allRows.filter((row) => {
      if (!query) return true;

      return (
        row.fileName.toLowerCase().includes(query) ||
        row.clientId.toLowerCase().includes(query) ||
        row.requestId.toLowerCase().includes(query) ||
        row.lastModifiedBy.toLowerCase().includes(query) ||
        row.status.toLowerCase().includes(query) ||
        (row.statusDetails?.model || '').toLowerCase().includes(query) ||
        (row.statusDetails?.message || '').toLowerCase().includes(query) ||
        (row.statusDetails?.requestId || '').toLowerCase().includes(query) ||
        (row.statusDetails?.clientRequestId || '').toLowerCase().includes(query) ||
        (row.statusDetails?.fileName || '').toLowerCase().includes(query)
      );
    });

    if (!this.sortColumn) {
      this.displayedRows = filtered;
      return;
    }

    filtered = filtered.sort((a, b) => {
      const left = a[this.sortColumn!];
      const right = b[this.sortColumn!];

      if (this.sortColumn === 'lastModifiedOn') {
        const leftDate = new Date(String(left)).getTime();
        const rightDate = new Date(String(right)).getTime();
        if (leftDate === rightDate) return 0;
        return this.sortDirection === 'asc' ? leftDate - rightDate : rightDate - leftDate;
      }

      if (typeof left === 'number' && typeof right === 'number') {
        return this.sortDirection === 'asc' ? left - right : right - left;
      }

      if (this.sortColumn === 'pageCount' || this.sortColumn === 'durationSeconds') {
        const leftNumber = typeof left === 'number' ? left : Number.NEGATIVE_INFINITY;
        const rightNumber = typeof right === 'number' ? right : Number.NEGATIVE_INFINITY;
        if (leftNumber === rightNumber) return 0;
        return this.sortDirection === 'asc' ? leftNumber - rightNumber : rightNumber - leftNumber;
      }

      const leftText = String(left).toLowerCase();
      const rightText = String(right).toLowerCase();
      const comparison = leftText.localeCompare(rightText);
      return this.sortDirection === 'asc' ? comparison : -comparison;
    });

    this.displayedRows = filtered;
  }

  private fetchSequencingRequests(): void {
    const epoch = ++this.sequencingRequestEpoch;

    if (this.selectedTimeFilter !== 'custom') {
      this.applyPresetDateWindow(this.selectedTimeFilter);
      this.saveSharedDateFilterState();
    }

    const startUtc = this.toUtcPayloadValue(this.startDate);
    const endUtc = this.toUtcPayloadValue(this.endDate);

    if (!startUtc || !endUtc) {
      this.sequencingRequestSub?.unsubscribe();
      this.isLoading = false;
      this.cdr.detectChanges();
      this.allRows = [];
      this.applyFiltersAndSort();
      return;
    }

    this.isLoading = true;
    this.cdr.detectChanges();
    this.sequencingRequestSub?.unsubscribe();

    const fetchWithSort = (sortBy: 'started_at' | 'completed_at'): void => {
      this.sequencingRequestSub?.unsubscribe();
      this.sequencingRequestSub = this.truthManagement.getSequencingRequestDetails({
        client_id: this.getCurrentClientId(),
        start_date: startUtc,
        end_date: endUtc,
        sort_by: sortBy,
        sort_dir: 'desc',
        offset: 0,
        limit: 100,
      }).subscribe({
        next: (response) => {
          if (epoch !== this.sequencingRequestEpoch) {
            return;
          }

          const requests = response?.data?.requests ?? [];
          this.allRows = requests.map((item) => this.mapSequencingItemToIndexingRow(item));
          this.applyFiltersAndSort();
          this.isLoading = false;
          this.cdr.detectChanges();
        },
        error: () => {
          if (epoch !== this.sequencingRequestEpoch) {
            return;
          }

          if (sortBy === 'started_at') {
            fetchWithSort('completed_at');
            return;
          }

          this.allRows = [];
          this.applyFiltersAndSort();
          this.isLoading = false;
          this.cdr.detectChanges();
        },
      });
    };

    fetchWithSort('started_at');
  }

  private mapSequencingItemToIndexingRow(item: SequencingRequestDetailsItem): IndexingRow {
    const fileName = this.normalizeDisplayFileName(item.file_name);
    const docsValue = item['docs'];
    const docs = typeof docsValue === 'number' && Number.isFinite(docsValue) ? String(docsValue) : '-';
    const miscPages =
      typeof item['misc_pages'] === 'number' && Number.isFinite(item['misc_pages'])
        ? (item['misc_pages'] > 0 ? String(item['misc_pages']) : '—')
        : '—';

    return {
      loanNumber: '',
      fileName,
      clientId: String(item.client_request_id ?? ''),
      requestId: String(item.request_id ?? ''),
      lastModifiedOn: String(item.started_at ?? ''),
      lastModifiedBy: String(item.owner ?? ''),
      pageCount: typeof item.page_count === 'number' ? item.page_count : null,
      miscPages,
      docs,
      durationSeconds: this.resolveDurationSeconds(item),
      status: this.normalizeApiStatus(item.status),
      hilStatus: this.normalizeHilStatus(item.hil_status),
      actions: 'icons',
      statusDetails: {
        clientRequestId: String(item.client_request_id ?? ''),
        requestId: String(item.request_id ?? ''),
        owner: String(item.owner ?? ''),
        ocrCost: item.ocr_cost ?? null,
        llmCost: item.llm_cost ?? null,
        fileName,
        model: String(item.model ?? ''),
        message: String(item.message ?? ''),
        pageCount: typeof item.page_count === 'number' ? item.page_count : null,
      },
    };
  }

  formatDurationInMinutes(durationSeconds: number | null): string {
    if (typeof durationSeconds !== 'number' || !Number.isFinite(durationSeconds) || durationSeconds < 0) {
      return '-';
    }

    const totalSeconds = Math.round(durationSeconds);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    }

    return `${seconds}s`;
  }

  private resolveDurationSeconds(item: SequencingRequestDetailsItem): number | null {
    const durationFromApi = item['duration_seconds'];
    if (typeof durationFromApi === 'number' && Number.isFinite(durationFromApi) && durationFromApi >= 0) {
      return durationFromApi;
    }

    return null;
  }

  private normalizeDisplayFileName(value: unknown): string {
    const normalized = String(value ?? '')
      .replace(/^[\s\u200B\uFEFF]+/u, '')
      .replace(/[\s\u200B\uFEFF]+$/u, '');

    return normalized || '-';
  }

  private normalizeApiStatus(status: unknown): IndexingRow['status'] {
    const value = String(status ?? '').trim().toLowerCase();
    if (value === 'completed') {
      return 'Completed';
    }
    if (value === 'failed') {
      return 'Failed';
    }

    return 'Active';
  }

  private toUtcPayloadValue(localDateTime: string): string {
    const date = this.parseLocalDateTime(localDateTime);
    if (!date) {
      return '';
    }

    const now = new Date();
    date.setSeconds(now.getSeconds(), now.getMilliseconds());
    return date.toISOString();
  }

  private commitPopupSelection(): void {
    if (!this.activeDateTimePopup || !this.popupDate) {
      return;
    }

    const datePart = this.formatDatePartFromDate(this.popupDate);
    const localDateTime = this.buildLocalDateTime(datePart, this.popupHour12, this.popupMinute, this.popupPeriod);
    const currentValue = this.activeDateTimePopup === 'start' ? this.startDate : this.endDate;

    if (localDateTime === currentValue) {
      this.syncDateDisplayValues();
      this.updateEndDateInvalidState();
      return;
    }

    if (this.activeDateTimePopup === 'start') {
      this.startDate = localDateTime;
      this.startHour12 = this.popupHour12;
      this.startMinute = this.popupMinute;
      this.startPeriod = this.popupPeriod;
    } else {
      this.endDate = localDateTime;
      this.endHour12 = this.popupHour12;
      this.endMinute = this.popupMinute;
      this.endPeriod = this.popupPeriod;
    }

    this.syncDateDisplayValues();
    this.selectedTimeFilter = 'custom';
    this.popupDirty = true;
    this.updateEndDateInvalidState();
  }

  private removeCalendarNavTitles(): void {
    setTimeout(() => {
      const navButtons = document.querySelectorAll('.date-time-popup-panel .mat-calendar-previous-button, .date-time-popup-panel .mat-calendar-next-button');
      navButtons.forEach((button) => {
        if (button instanceof HTMLElement) {
          button.removeAttribute('title');
        }
      });
    }, 0);
  }

  private loadSharedDateFilterState(): SharedDateFilterState | null {
    const raw = sessionStorage.getItem(SHARED_DATE_FILTER_KEY);
    if (!raw) {
      return null;
    }

    try {
      const parsed = JSON.parse(raw) as Partial<SharedDateFilterState>;
      const selected = parsed.selectedTimeFilter;
      if (!selected || !['today', 'last7', 'last30', 'custom'].includes(selected)) {
        return null;
      }

      const start = typeof parsed.startDate === 'string' ? parsed.startDate : '';
      const end = typeof parsed.endDate === 'string' ? parsed.endDate : '';
      if (!this.parseLocalDateTime(start) || !this.parseLocalDateTime(end)) {
        return null;
      }

      return {
        selectedTimeFilter: selected as TimeFilter,
        startDate: start,
        endDate: end,
        startDateDisplay: typeof parsed.startDateDisplay === 'string' ? parsed.startDateDisplay : this.formatDateForDisplay(start),
        endDateDisplay: typeof parsed.endDateDisplay === 'string' ? parsed.endDateDisplay : this.formatDateForDisplay(end),
        updatedAt: typeof parsed.updatedAt === 'number' ? parsed.updatedAt : Date.now(),
      };
    } catch {
      return null;
    }
  }

  private applySharedDateFilterState(state: SharedDateFilterState): void {
    this.selectedTimeFilter = state.selectedTimeFilter;
    this.startDate = state.startDate;
    this.endDate = state.endDate;
    this.startDateDisplay = state.startDateDisplay || this.formatDateForDisplay(state.startDate);
    this.endDateDisplay = state.endDateDisplay || this.formatDateForDisplay(state.endDate);
    this.syncTimePartsFromDateTimes();
    this.updateEndDateInvalidState();
  }

  private applyPresetDateWindow(filter: Exclude<TimeFilter, 'custom'>): void {
    const now = new Date();
    let start = new Date(now.getTime() - 24 * 60 * 60 * 1000);

    if (filter === 'last7') {
      start = new Date(now.getTime() - 168 * 60 * 60 * 1000);
    } else if (filter === 'last30') {
      start = new Date(now.getTime() - 720 * 60 * 60 * 1000);
    }

    this.startDate = this.formatDateTimeForInput(start);
    this.endDate = this.formatDateTimeForInput(now);
    this.syncTimePartsFromDateTimes();
    this.syncDateDisplayValues();
  }

  private saveSharedDateFilterState(): void {
    const payload: SharedDateFilterState = {
      selectedTimeFilter: this.selectedTimeFilter,
      startDate: this.startDate,
      endDate: this.endDate,
      startDateDisplay: this.startDateDisplay,
      endDateDisplay: this.endDateDisplay,
      updatedAt: Date.now(),
    };

    sessionStorage.setItem(SHARED_DATE_FILTER_KEY, JSON.stringify(payload));
  }

  private isSharedDateFilterRoute(url: string): boolean {
    const path = (url || '').split('?')[0].split('#')[0];
    return (
      path === '/indexing' ||
      path.startsWith('/operations-request-analytics') ||
      path === '/extraction-reports' ||
      path.startsWith('/extraction-reports/') ||
      path === '/source'
    );
  }

  private formatDateTimeForInput(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }

  private parseLocalDateTime(value: string): Date | null {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
    if (!match) {
      return null;
    }

    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const hours = Number(match[4]);
    const minutes = Number(match[5]);

    const date = new Date(year, month - 1, day, hours, minutes, 0, 0);
    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
      return null;
    }

    return date;
  }

  private formatDateTimeForDisplay(date: Date): string {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const isPm = date.getHours() >= 12;
    const hour12 = date.getHours() % 12 || 12;
    const period = isPm ? 'PM' : 'AM';
    return `${month}/${day}/${year} ${String(hour12).padStart(2, '0')}:${minutes} ${period}`;
  }

  private buildLocalDateTime(datePart: string, hour12: string, minute: string, period: 'AM' | 'PM'): string {
    const converted = this.convertDisplayToLocalDateTime(`${this.toDisplayDate(datePart)} ${hour12}:${minute} ${period}`);
    if (converted) {
      return converted;
    }

    const hour24 = period === 'PM' ? (Number(hour12) % 12) + 12 : Number(hour12) % 12;
    return `${datePart}T${String(hour24).padStart(2, '0')}:${minute}`;
  }

  private toDisplayDate(datePart: string): string {
    const parts = datePart.split('-');
    if (parts.length !== 3) {
      return datePart;
    }

    return `${parts[1]}/${parts[2]}/${parts[0]}`;
  }

  private formatDatePartFromDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private syncTimePartsFromDateTimes(): void {
    const start = this.parseLocalDateTime(this.startDate);
    if (start) {
      const startIsPm = start.getHours() >= 12;
      this.startPeriod = startIsPm ? 'PM' : 'AM';
      this.startHour12 = String(start.getHours() % 12 || 12).padStart(2, '0');
      this.startMinute = String(start.getMinutes()).padStart(2, '0');
    }

    const end = this.parseLocalDateTime(this.endDate);
    if (end) {
      const endIsPm = end.getHours() >= 12;
      this.endPeriod = endIsPm ? 'PM' : 'AM';
      this.endHour12 = String(end.getHours() % 12 || 12).padStart(2, '0');
      this.endMinute = String(end.getMinutes()).padStart(2, '0');
    }
  }

  private formatDateForDisplay(dateValue: string): string {
    if (!dateValue) {
      return '';
    }

    const date = this.parseLocalDateTime(dateValue);
    if (!date) {
      return '';
    }

    return this.formatDateTimeForDisplay(date);
  }

  private syncDateDisplayValues(): void {
    this.startDateDisplay = this.formatDateForDisplay(this.startDate);
    this.endDateDisplay = this.formatDateForDisplay(this.endDate);
  }

  private normalizeDisplayDateTimeInput(value: string): string {
    return value.replace(/\s+/g, ' ').toUpperCase();
  }

  private convertDisplayToLocalDateTime(value: string): string | null {
    const trimmed = value.trim().toUpperCase();
    const match = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2})\s*(AM|PM))?$/);
    if (!match) {
      return null;
    }

    const month = Number(match[1]);
    const day = Number(match[2]);
    const year = Number(match[3]);
    if (month < 1 || month > 12 || year < 1900 || year > 2100) {
      return null;
    }

    let hours = 0;
    let minutes = 0;
    if (match[4] !== undefined && match[5] !== undefined && match[6] !== undefined) {
      const hour12 = Number(match[4]);
      minutes = Number(match[5]);
      const period = match[6];
      if (hour12 < 1 || hour12 > 12 || minutes < 0 || minutes > 59) {
        return null;
      }
      hours = period === 'PM' ? (hour12 % 12) + 12 : hour12 % 12;
    }

    const date = new Date(year, month - 1, day, hours, minutes, 0, 0);
    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
      return null;
    }

    return this.formatDateTimeForInput(date);
  }

  private updateEndDateInvalidState(): void {
    const start = this.parseLocalDateTime(this.startDate);
    const end = this.parseLocalDateTime(this.endDate);
    const now = new Date();

    if (!start || !end) {
      this.endDateInvalid = false;
      this.dateRangeValidationMessage = '';
      return;
    }

    if (end.getTime() < start.getTime()) {
      this.endDateInvalid = true;
      this.dateRangeValidationMessage = 'End date/time must be greater than start date/time.';
      if (!this.hasShownInvalidDateRangeToast) {
        this.notificationService.showError('End date/time must be greater than start date/time.');
        this.hasShownInvalidDateRangeToast = true;
      }
      return;
    }

    this.endDateInvalid = end.getTime() > now.getTime();
    this.dateRangeValidationMessage = '';
  }

  private validateDateRange(): boolean {
    if (!this.startDate || !this.endDate) {
      this.dateRangeValidationMessage = '';
      return false;
    }

    const start = this.parseLocalDateTime(this.startDate);
    const end = this.parseLocalDateTime(this.endDate);
    if (!start || !end) {
      this.endDateInvalid = true;
      this.dateRangeValidationMessage = '';
      this.notificationService.showError('Invalid datetime range. Please use valid date and time values.');
      return false;
    }

    if (end.getTime() < start.getTime()) {
      this.endDateInvalid = true;
      this.dateRangeValidationMessage = 'End date/time must be greater than start date/time.';
      if (!this.hasShownInvalidDateRangeToast) {
        this.notificationService.showError('End date/time must be greater than start date/time.');
        this.hasShownInvalidDateRangeToast = true;
      }
      return false;
    }

    const now = new Date();
    if (end.getTime() > now.getTime()) {
      this.endDateInvalid = true;
      this.dateRangeValidationMessage = '';
      this.notificationService.showError('Invalid date picked. End datetime cannot be greater than current datetime.');
      return false;
    }

    this.endDateInvalid = false;
    this.dateRangeValidationMessage = '';
    return true;
  }

  private assignAddLoanUploadFromFileList(files: FileList | null): void {
    this.addLoanFormError = '';
    this.addLoanFileError = '';

    if (!files || files.length === 0) {
      this.addLoanSelectedFile = null;
      this.addLoanSelectedFileName = '';
      return;
    }

    if (files.length > 1) {
      this.addLoanSelectedFile = null;
      this.addLoanSelectedFileName = '';
      this.addLoanFileError = 'Only one file is allowed.';
      return;
    }

    const file = files[0];
    if (!this.isAcceptedUploadFile(file)) {
      this.addLoanSelectedFile = null;
      this.addLoanSelectedFileName = '';
      this.addLoanFileError = 'Only .zip or .pdf files are accepted.';
      return;
    }

    this.addLoanSelectedFile = file;
    this.addLoanSelectedFileName = file.name;
  }

  private isAcceptedUploadFile(file: File): boolean {
    const name = file.name.toLowerCase();
    if (!ACCEPTED_UPLOAD_EXTENSIONS.some((ext) => name.endsWith(ext))) {
      return false;
    }

    if (!file.type) {
      return true;
    }

    return ACCEPTED_UPLOAD_MIME_TYPES.has(file.type.toLowerCase());
  }

  private readonly knownPackageFieldTypes = new Set<PackageFieldType>([
    'text', 'textarea', 'number', 'checkbox', 'radio', 'select', 'multiselect', 'date', 'datetime', 'json',
  ]);

  private fetchAddPackageSchema(): void {
    this.isPackageSchemaLoading = true;
    this.addLoanFormError = '';
    this.addPackageSchemaSub?.unsubscribe();
    this.addPackageSchemaSub = this.truthManagement.getClientMetadata(this.getCurrentClientId()).subscribe({
      next: (response) => {
        const fields = this.normalizePackageAttributes(response?.metadata?.package_attributes ?? []);
        this.addPackageSchema = fields;
        this.initializeAddPackageValues(fields);
        this.isPackageSchemaLoading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.addPackageSchema = [];
        this.addPackageValues = {};
        this.isPackageSchemaLoading = false;
        this.addLoanFormError = 'Unable to load package fields. Please try again.';
        this.cdr.detectChanges();
      },
    });
  }

  private normalizePackageAttributes(raw: Array<Record<string, unknown>>): PackageField[] {
    return raw
      .filter((attr) => typeof attr?.['key'] === 'string' && typeof attr?.['label'] === 'string')
      .map((attr) => {
        const fieldType = attr['field_type'];
        return {
          key: attr['key'] as string,
          label: attr['label'] as string,
          field_type: this.knownPackageFieldTypes.has(fieldType as PackageFieldType)
            ? (fieldType as PackageFieldType)
            : 'text',
          description: typeof attr['description'] === 'string' ? attr['description'] as string : undefined,
          placeholder: typeof attr['placeholder'] === 'string' ? attr['placeholder'] as string : undefined,
          required: attr['required'] === true,
          default: attr['default'],
          rows: typeof attr['rows'] === 'number' ? attr['rows'] as number : undefined,
          is_list: attr['is_list'] === true,
          options: Array.isArray(attr['options'])
            ? (attr['options'] as Array<Record<string, unknown>>)
                .filter((opt) => typeof opt?.['value'] === 'string' && typeof opt?.['label'] === 'string')
                .map((opt) => ({ value: opt['value'] as string, label: opt['label'] as string }))
            : undefined,
          conditional_on: (attr['conditional_on'] && typeof attr['conditional_on'] === 'object')
            ? attr['conditional_on'] as Record<string, unknown>
            : undefined,
        };
      });
  }

  private initializeAddPackageValues(schema: PackageField[]): void {
    const seed: Record<string, unknown> = {};
    const jsonDrafts: Record<string, string> = {};

    for (const field of schema) {
      const defaultValue = this.cloneDefaultValue(field.default);

      if (field.field_type === 'json') {
        seed[field.key] = this.toKvRecord(defaultValue);
      } else if (field.field_type === 'multiselect') {
        seed[field.key] = Array.isArray(defaultValue) ? [...(defaultValue as string[])] : [];
      } else if (field.field_type === 'checkbox') {
        seed[field.key] = defaultValue === true;
      } else if (field.field_type === 'textarea' && field.is_list) {
        const listDefault = Array.isArray(defaultValue) ? (defaultValue as unknown[]).map((v) => String(v ?? '')) : [];
        seed[field.key] = listDefault;
      } else if (defaultValue === undefined) {
        seed[field.key] = field.field_type === 'number' ? null : '';
      } else {
        seed[field.key] = defaultValue;
      }
    }

    this.addPackageValues = seed;
    this.addPackageJsonDrafts = jsonDrafts;
  }

  private cloneDefaultValue(value: unknown): unknown {
    if (value === null || value === undefined) {
      return value;
    }
    if (Array.isArray(value)) {
      return value.slice();
    }
    if (typeof value === 'object') {
      return { ...(value as Record<string, unknown>) };
    }
    return value;
  }

  private stringifyJsonValue(value: unknown): string {
    if (value === null || value === undefined) {
      return '';
    }
    if (typeof value === 'object' && Object.keys(value as Record<string, unknown>).length === 0
      && !Array.isArray(value)) {
      return '';
    }
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return '';
    }
  }

  shouldShowPackageField(field: PackageField): boolean {
    const condition = field.conditional_on;
    if (!condition) {
      return true;
    }

    return Object.entries(condition).every(([dependencyKey, expectedValue]) => {
      return this.addPackageValues[dependencyKey] === expectedValue;
    });
  }

  asStringArray(value: unknown): string[] {
    return Array.isArray(value) ? (value as string[]) : [];
  }

  togglePackageMultiselect(field: PackageField, optionValue: string, event: Event): void {
    const checked = (event.target as HTMLInputElement | null)?.checked === true;
    const current = this.asStringArray(this.addPackageValues[field.key]);
    if (checked) {
      if (!current.includes(optionValue)) {
        this.addPackageValues[field.key] = [...current, optionValue];
      }
    } else {
      this.addPackageValues[field.key] = current.filter((v) => v !== optionValue);
    }
    if (this.addPackageFieldErrors[field.key]) {
      delete this.addPackageFieldErrors[field.key];
    }
  }

  addPackageListItem(field: PackageField): void {
    const current = this.asStringArray(this.addPackageValues[field.key]);
    this.addPackageValues[field.key] = [...current, ''];
  }

  removePackageListItem(field: PackageField, index: number): void {
    const current = this.asStringArray(this.addPackageValues[field.key]);
    if (current.length <= 1) {
      return;
    }
    this.addPackageValues[field.key] = current.filter((_, i) => i !== index);
  }

  updatePackageListItem(field: PackageField, index: number, value: string): void {
    const current = this.asStringArray(this.addPackageValues[field.key]).slice();
    current[index] = value;
    this.addPackageValues[field.key] = current;
  }

  setPackageChipDraft(key: string, value: string): void {
    this.addPackageChipDrafts[key] = value;
  }

  commitPackageChip(field: PackageField, event?: Event): void {
    if (event) {
      event.preventDefault();
    }
    const draft = (this.addPackageChipDrafts[field.key] ?? '').trim();
    if (!draft) {
      return;
    }
    const current = this.asStringArray(this.addPackageValues[field.key]);
    if (current.some((v) => v.trim().toLowerCase() === draft.toLowerCase())) {
      this.addPackageChipDrafts[field.key] = '';
      return;
    }
    this.addPackageValues[field.key] = [...current, draft];
    this.addPackageChipDrafts[field.key] = '';
  }

  removePackageChip(field: PackageField, index: number): void {
    const current = this.asStringArray(this.addPackageValues[field.key]);
    this.addPackageValues[field.key] = current.filter((_, i) => i !== index);
  }

  private toKvRecord(value: unknown): Record<string, string> {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return {};
    }
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (typeof k !== 'string' || !k) {
        continue;
      }
      out[k] = v === null || v === undefined ? '' : String(v);
    }
    return out;
  }

  getPackageKvEntries(field: PackageField): Array<{ key: string; value: string }> {
    const map = this.toKvRecord(this.addPackageValues[field.key]);
    return Object.keys(map).map((k) => ({ key: k, value: map[k] }));
  }

  setPackageKvKeyDraft(key: string, value: string): void {
    const current = this.addPackageKvDrafts[key] ?? { key: '', value: '' };
    this.addPackageKvDrafts[key] = { key: value, value: current.value };
  }

  setPackageKvValueDraft(key: string, value: string): void {
    const current = this.addPackageKvDrafts[key] ?? { key: '', value: '' };
    this.addPackageKvDrafts[key] = { key: current.key, value: value };
  }

  commitPackageKv(field: PackageField, event?: Event): void {
    if (event) {
      event.preventDefault();
    }
    const draft = this.addPackageKvDrafts[field.key] ?? { key: '', value: '' };
    const key = draft.key.trim();
    const value = draft.value;
    if (!key) {
      return;
    }
    const current = this.toKvRecord(this.addPackageValues[field.key]);
    current[key] = value;
    this.addPackageValues[field.key] = current;
    this.addPackageKvDrafts[field.key] = { key: '', value: '' };
  }

  removePackageKv(field: PackageField, key: string): void {
    const current = this.toKvRecord(this.addPackageValues[field.key]);
    delete current[key];
    this.addPackageValues[field.key] = current;
  }

  getPackageJsonString(key: string): string {
    return this.addPackageJsonDrafts[key] ?? '';
  }

  setPackageJson(key: string, raw: string): void {
    this.addPackageJsonDrafts[key] = raw;
    const trimmed = raw.trim();

    if (!trimmed) {
      this.addPackageValues[key] = {};
      delete this.addPackageJsonErrors[key];
      return;
    }

    try {
      this.addPackageValues[key] = JSON.parse(trimmed);
      delete this.addPackageJsonErrors[key];
    } catch (error) {
      this.addPackageJsonErrors[key] = 'Invalid JSON.';
    }
  }

  private validateAddPackageForm(): boolean {
    this.addLoanFormError = '';
    this.addPackageFieldErrors = {};

    for (const field of this.addPackageSchema) {
      if (!this.shouldShowPackageField(field)) {
        continue;
      }
      if (!field.required) {
        continue;
      }

      const value = this.addPackageValues[field.key];

      if (field.is_list) {
        const items = this.asStringArray(value).filter((v) => typeof v === 'string' && v.trim());
        if (items.length === 0) {
          this.addPackageFieldErrors[field.key] = `${field.label} is required.`;
        }
        continue;
      }

      switch (field.field_type) {
        case 'text':
        case 'select':
        case 'radio':
        case 'date':
        case 'datetime':
        case 'textarea':
          if (typeof value !== 'string' || !value.trim()) {
            this.addPackageFieldErrors[field.key] = `${field.label} is required.`;
          }
          break;
        case 'number':
          if (value === null || value === undefined || value === '' || Number.isNaN(Number(value))) {
            this.addPackageFieldErrors[field.key] = `${field.label} is required.`;
          }
          break;
        case 'checkbox':
          if (value !== true) {
            this.addPackageFieldErrors[field.key] = `${field.label} is required.`;
          }
          break;
        case 'multiselect':
          if (!Array.isArray(value) || value.length === 0) {
            this.addPackageFieldErrors[field.key] = `Select at least one ${field.label}.`;
          }
          break;
        case 'json':
          if (Object.keys(this.toKvRecord(this.addPackageValues[field.key])).length === 0) {
            this.addPackageFieldErrors[field.key] = `${field.label} is required.`;
          }
          break;
      }
    }

    if (Object.keys(this.addPackageJsonErrors).length > 0) {
      this.addLoanFormError = 'Please fix the invalid JSON fields before continuing.';
      return false;
    }

    if (Object.keys(this.addPackageFieldErrors).length > 0) {
      this.addLoanFormError = 'Please fill in the required fields.';
      return false;
    }

    if (!this.addLoanSelectedFile) {
      this.addLoanFileError = 'Please upload one .zip or .pdf file.';
      return false;
    }

    return true;
  }

  private validateAddLoanForm(): boolean {
    this.addLoanFormError = '';

    if (!this.addLoanLoanNumber.trim()) {
      this.addLoanFormError = 'Loan Number is required.';
      return false;
    }

    if (!this.addLoanBorrowerNames.trim()) {
      this.addLoanFormError = 'Borrower Name(s) Comma Separated is required.';
      return false;
    }

    if (!this.addLoanSelectedPriority) {
      this.addLoanFormError = 'Priority is required.';
      return false;
    }

    if (!this.addLoanSelectedChecklistType) {
      this.addLoanFormError = 'Checklist Type is required.';
      return false;
    }

    const hasExistingFileReference = this.loanModalMode === 'edit' && !!this.addLoanSelectedFileName;
    if (!this.addLoanSelectedFile && !hasExistingFileReference) {
      this.addLoanFileError = 'Please upload one .zip or .pdf file.';
      return false;
    }

    return true;
  }

  get canSubmitAddPackage(): boolean {
    if (this.isSubmittingAddPackage) { return false; }
    return this.loanModalMode === 'edit'
      ? this.hasRequiredEditFieldsFilled()
      : this.hasRequiredAddFieldsFilled();
  }

  private hasRequiredAddFieldsFilled(): boolean {
    if (!this.addLoanSelectedFile) { return false; }
    if (Object.keys(this.addPackageJsonErrors).length > 0) { return false; }
    for (const field of this.addPackageSchema) {
      if (!field.required || !this.shouldShowPackageField(field)) { continue; }
      if (this.isPackageFieldEmpty(field, this.addPackageValues[field.key])) { return false; }
    }
    return true;
  }

  private hasRequiredEditFieldsFilled(): boolean {
    if (!this.addLoanLoanNumber?.trim()) { return false; }
    if (!this.addLoanBorrowerNames?.trim()) { return false; }
    if (!this.addLoanSelectedPriority) { return false; }
    if (!this.addLoanSelectedChecklistType) { return false; }
    const hasExistingFileReference = !!this.addLoanSelectedFileName;
    if (!this.addLoanSelectedFile && !hasExistingFileReference) { return false; }
    return true;
  }

  private isPackageFieldEmpty(field: PackageField, value: unknown): boolean {
    if (field.is_list) {
      const items = this.asStringArray(value).filter((v) => typeof v === 'string' && v.trim());
      return items.length === 0;
    }
    switch (field.field_type) {
      case 'text':
      case 'select':
      case 'radio':
      case 'date':
      case 'datetime':
      case 'textarea':
        return typeof value !== 'string' || !value.trim();
      case 'number':
        return value === null || value === undefined || value === '' || Number.isNaN(Number(value));
      case 'checkbox':
        return value !== true;
      case 'multiselect':
        return !Array.isArray(value) || value.length === 0;
      case 'json':
        return Object.keys(this.toKvRecord(this.addPackageValues[field.key])).length === 0;
      default:
        return false;
    }
  }

  private resetAddLoanModalForm(): void {
    this.addLoanLoanNumber = '';
    this.addLoanBorrowerNames = '';
    this.addLoanSelectedPriority = this.addLoanPriorityOptions[0];
    this.addLoanSelectedChecklistType = this.addLoanChecklistTypeOptions[0];
    this.addLoanSelectedFile = null;
    this.addLoanSelectedFileName = '';
    this.addLoanFileError = '';
    this.addLoanFormError = '';
    this.isAddLoanDropActive = false;

    this.addPackageValues = {};
    this.addPackageChipDrafts = {};
    this.addPackageKvDrafts = {};
    this.addPackageJsonDrafts = {};
    this.addPackageJsonErrors = {};
    this.addPackageFieldErrors = {};
    this.packageDateDisplayByKey = {};
    this.packageDateSelectedByKey = {};
    this.packageDateInvalidByKey = {};
    this.activePackageDatePopupKey = null;
  }

  private prefillLoanModalForEdit(row: IndexingRow): void {
    this.addLoanLoanNumber = row.loanNumber;
    this.addLoanBorrowerNames = row.lastModifiedBy;
    this.addLoanSelectedPriority = this.addLoanPriorityOptions[0];
    this.addLoanSelectedChecklistType = this.addLoanChecklistTypeOptions[0];
    this.addLoanSelectedFileName = `${row.loanNumber}.zip`;
  }
}
